/**
 * Kontext identity (S11, F4AI-17). Provider drží `Identity` z
 * `nostr/keys.ts` (nsec z `localStorage` `forum4ai_nsec`) a dáva ju
 * Headeru aj Composeru. Testy injectnú `storage` (memory) alebo hotovú
 * `identity`, takže sa nedotknú skutočného úložiska.
 */
import { createContext, useCallback, useContext, useMemo, useState, type JSX, type ReactNode } from 'react';

import {
  browserStorage,
  clearIdentity,
  loadOrCreateIdentity,
  type Identity,
  type KeyStorage,
} from './keys.js';

export interface IdentityValue {
  identity: Identity | null;
  error: string | null;
  /** zahodí uložený nsec a vygeneruje novú identitu */
  reset: () => void;
}

const IdentityContext = createContext<IdentityValue>({ identity: null, error: null, reset: () => undefined });

export function IdentityProvider({
  storage,
  identity: identityProp,
  children,
}: {
  storage?: KeyStorage | null;
  identity?: Identity;
  children: ReactNode;
}): JSX.Element {
  const [seed, setSeed] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const identity = useMemo(() => {
    if (identityProp !== undefined) return identityProp;
    try {
      return loadOrCreateIdentity(storage ?? browserStorage());
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      return null;
    }
    // `seed` patrí do deps — reset musí vynútiť regeneráciu identity
    // (react-hooks plugin tu nie je nainštalovaný, deps ladíme ručne)
  }, [identityProp, storage, seed]);

  const reset = useCallback(() => {
    try {
      clearIdentity(storage ?? browserStorage());
    } catch {
      /* úložisko nedostupné — aj tak vygenerujeme novú identitu */
    }
    setError(null);
    setSeed((value) => value + 1);
  }, [storage]);

  return <IdentityContext.Provider value={{ identity, error, reset }}>{children}</IdentityContext.Provider>;
}

export function useIdentity(): IdentityValue {
  return useContext(IdentityContext);
}
